import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, CheckCircle2, AlertCircle, Loader2, Lock, Receipt, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast, Toaster } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PublicInvoicePay = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paidSuccess, setPaidSuccess] = useState(false);
  const [payChoice, setPayChoice] = useState("balance");
  const [customAmount, setCustomAmount] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  // True when the customer just returned from Stripe — keep the success
  // screen instead of bouncing straight to the PDF.
  const freshPayRef = useRef(new URLSearchParams(window.location.search).has("payment"));

  const pdfUrl = `${API}/invoices/public/${token}/pdf`;

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API}/portal/pay/${token}`);
      setInvoice(res.data);
      // Already-paid invoice: open the paid invoice PDF directly.
      if (res.data.status === "paid" && !freshPayRef.current) {
        setRedirecting(true);
        window.location.replace(pdfUrl);
        return;
      }
      setPayChoice(res.data?.payment_options?.deposit && (res.data?.deposit_due || 0) > 0 ? "deposit" : "balance");
    } catch (e) {
      setError(e.response?.data?.detail || "This payment link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handle success/cancel return from Stripe
  useEffect(() => {
    const status = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (status === "success" && sessionId) {
      setVerifying(true);
      (async () => {
        try {
          const res = await axios.get(`${API}/portal/pay/${token}/status?session_id=${sessionId}`);
          if (res.data.payment_status === "paid") {
            if (res.data.invoice_status && res.data.invoice_status !== "paid") {
              toast.success("Deposit received — thank you!");
              fetchInvoice();
            } else {
              setPaidSuccess(true);
              toast.success("Payment successful! Thank you.");
              fetchInvoice();
            }
          } else {
            toast.info("Payment is processing. This may take a moment.");
          }
        } catch {
          toast.error("Could not verify payment");
        } finally {
          setVerifying(false);
          setSearchParams({});
        }
      })();
    } else if (status === "cancelled") {
      toast.info("Payment was cancelled");
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handlePay = async () => {
    if (baseAmount <= 0) { toast.error("Enter a valid amount to pay"); return; }
    if (baseAmount > balance + 0.005) { toast.error(`Amount can't exceed the remaining balance ($${balance.toFixed(2)})`); return; }
    setPaying(true);
    try {
      const res = await axios.post(`${API}/portal/pay/${token}/checkout`, {
        origin_url: window.location.origin,
        tip_amount: tipAmount > 0 ? Number(tipAmount) : 0,
        customer_email: email || undefined,
        amount: baseAmount,
        payment_kind: payChoice,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start payment");
      setPaying(false);
    }
  };

  const total = invoice?.total || 0;
  const alreadyPaid = invoice?.amount_paid || 0;
  const balance = invoice?.balance_due !== undefined ? invoice.balance_due : Math.max(total - alreadyPaid, 0);
  const depositDue = invoice?.deposit_due || 0;
  const payOpts = invoice?.payment_options || { balance: true };
  const showChoices = (payOpts.deposit && depositDue > 0) || payOpts.custom;
  const baseAmount = payChoice === "deposit"
    ? Math.min(depositDue, balance)
    : payChoice === "custom"
      ? (parseFloat(customAmount) || 0)
      : balance;
  const grandTotal = baseAmount + (Number(tipAmount) || 0);
  const brand = invoice?.brand_primary || "#0046BE";
  const accent = invoice?.brand_accent || "#FFCC00";

  if (loading || redirecting) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        {redirecting && <p className="text-sm text-slate-500" data-testid="guest-pay-pdf-redirect">Opening your paid invoice…</p>}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center border border-slate-100">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Link Not Valid</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === "paid" || paidSuccess;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-md mx-auto">
        {/* Business Header */}
        <div className="text-center mb-6">
          {invoice.business_logo_url ? (
            <div className="inline-flex items-center justify-center bg-white rounded-2xl px-5 py-3 mb-3 border border-slate-100 shadow-sm">
              <img src={invoice.business_logo_url} alt={invoice.business_name} className="h-14 object-contain" data-testid="guest-pay-logo" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: brand }}>
              <Receipt className="w-6 h-6 text-white" />
            </div>
          )}
          <h2 className="text-base font-bold text-slate-800">{invoice.business_name || "Invoice Payment"}</h2>
          {invoice.business_phone && <p className="text-xs text-slate-400 mt-0.5">{invoice.business_phone}</p>}
        </div>

        {/* Paid success card */}
        {isPaid ? (
          <div className="bg-white rounded-2xl overflow-hidden text-center border border-emerald-100" data-testid="guest-pay-success">
            <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${brand}, ${accent})` }} />
            <div className="p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Payment Received</h1>
            <p className="text-sm text-slate-500 mb-5">Thank you! A receipt has been emailed to you.</p>
            <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Invoice</span>
                <span className="font-semibold text-slate-700">#{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Amount Paid</span>
                <span className="font-semibold text-emerald-600">${(invoice.amount_paid || invoice.total).toFixed(2)}</span>
              </div>
            </div>
            <Button
              onClick={() => { window.location.href = pdfUrl; }}
              className="w-full h-11 mt-5 text-white font-semibold rounded-xl hover:opacity-90"
              style={{ backgroundColor: brand }}
              data-testid="guest-pay-view-pdf"
            >
              <FileText className="w-4 h-4 mr-2" /> View Paid Invoice (PDF)
            </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden border border-slate-100" data-testid="guest-pay-card">
            <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${brand}, ${accent})` }} />
            {/* Amount Header */}
            <div className="p-6 text-center border-b border-slate-100">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Amount Due</p>
              <p className="text-4xl font-bold text-slate-800 mt-2 font-['Outfit']" data-testid="guest-pay-amount">
                ${balance.toFixed(2)}
              </p>
              {alreadyPaid > 0 && (
                <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200" data-testid="guest-pay-partial-badge">
                  <span className="text-[11px] font-semibold text-amber-600">Partially Paid</span>
                  <span className="text-[11px] text-amber-500">· ${alreadyPaid.toFixed(2)} of ${total.toFixed(2)} received</span>
                </div>
              )}
              <p className="text-sm text-slate-500 mt-2">
                Invoice #{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}
              </p>
              {invoice.customer_name && <p className="text-xs text-slate-400 mt-1">Billed to {invoice.customer_name}</p>}
            </div>

            {/* Verifying banner */}
            {verifying && (
              <div className="bg-blue-50 border-b border-blue-100 p-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <p className="text-xs text-blue-700">Verifying your payment…</p>
              </div>
            )}

            {invoice.online_payments_enabled ? (
              <div className="p-6 space-y-4">
                {/* Payment amount choice (deposit / balance / custom) */}
                {showChoices && (
                  <div data-testid="guest-pay-choices">
                    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">How much would you like to pay?</label>
                    <div className="space-y-2 mt-2">
                      {payOpts.deposit && depositDue > 0 && (
                        <button
                          type="button"
                          onClick={() => setPayChoice("deposit")}
                          className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${payChoice === "deposit" ? "" : "border-slate-200 hover:border-slate-300"}`}
                          style={payChoice === "deposit" ? { borderColor: brand, backgroundColor: `${brand}14` } : undefined}
                          data-testid="guest-choice-deposit"
                        >
                          <div>
                            <p className="text-sm font-semibold text-slate-700">Required Deposit</p>
                            <p className="text-[11px] text-slate-400">Pay the deposit now, the rest later</p>
                          </div>
                          <span className="text-sm font-bold text-slate-800">${Math.min(depositDue, balance).toFixed(2)}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPayChoice("balance")}
                        className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${payChoice === "balance" ? "" : "border-slate-200 hover:border-slate-300"}`}
                        style={payChoice === "balance" ? { borderColor: brand, backgroundColor: `${brand}14` } : undefined}
                        data-testid="guest-choice-balance"
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-700">{alreadyPaid > 0 ? "Remaining Balance" : "Full Amount"}</p>
                          <p className="text-[11px] text-slate-400">Pays the invoice in full</p>
                        </div>
                        <span className="text-sm font-bold text-slate-800">${balance.toFixed(2)}</span>
                      </button>
                      {payOpts.custom && (
                        <button
                          type="button"
                          onClick={() => setPayChoice("custom")}
                          className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${payChoice === "custom" ? "" : "border-slate-200 hover:border-slate-300"}`}
                          style={payChoice === "custom" ? { borderColor: brand, backgroundColor: `${brand}14` } : undefined}
                          data-testid="guest-choice-custom"
                        >
                          <p className="text-sm font-semibold text-slate-700">Other Amount</p>
                          <span className="text-[11px] text-slate-400">up to ${balance.toFixed(2)}</span>
                        </button>
                      )}
                      {payChoice === "custom" && (
                        <Input
                          type="number" min="0" step="0.01"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder={`Enter amount (max $${balance.toFixed(2)})`}
                          className="h-10 text-sm rounded-lg"
                          data-testid="guest-custom-amount"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Optional tip */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Add a tip? (optional)</label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {[0, 5, 10, 15, 20].map((pct) => {
                      const amt = pct === 0 ? 0 : Math.round(baseAmount * pct) / 100;
                      const active = Number(tipAmount) === Number(amt);
                      return (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setTipAmount(amt)}
                          data-testid={`tip-${pct}`}
                          className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                          style={active ? { backgroundColor: brand, borderColor: brand } : undefined}
                        >
                          {pct === 0 ? "None" : `${pct}%`}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tipAmount || ""}
                      onChange={(e) => setTipAmount(Math.max(0, Number(e.target.value) || 0))}
                      placeholder="Custom tip ($)"
                      className="h-9 text-sm rounded-lg"
                      data-testid="guest-pay-tip-input"
                    />
                  </div>
                </div>

                {/* Email for receipt */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email for receipt (optional)</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-9 text-sm rounded-lg mt-1.5"
                    data-testid="guest-pay-email-input"
                  />
                </div>

                {/* Totals */}
                {(Number(tipAmount) > 0 || baseAmount !== balance) && (
                  <div className="pt-3 border-t border-slate-100 space-y-1">
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>{payChoice === "deposit" ? "Deposit" : "Payment"}</span><span>${baseAmount.toFixed(2)}</span>
                    </div>
                    {Number(tipAmount) > 0 && (
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>Tip</span><span>+${Number(tipAmount).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-slate-800 pt-1">
                      <span>Total</span><span data-testid="guest-pay-total">${grandTotal.toFixed(2)}</span>
                    </div>
                    {baseAmount < balance - 0.005 && baseAmount > 0 && (
                      <p className="text-[11px] text-amber-500 pt-1">
                        ${Math.max(balance - baseAmount, 0).toFixed(2)} will remain due after this payment
                      </p>
                    )}
                  </div>
                )}

                <Button
                  onClick={handlePay}
                  disabled={paying || baseAmount <= 0}
                  className="w-full h-11 text-white font-semibold rounded-xl hover:opacity-90"
                  style={{ backgroundColor: brand }}
                  data-testid="guest-pay-submit"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Pay ${grandTotal.toFixed(2)}
                </Button>

                <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-1">
                  <Lock className="w-3 h-3" />
                  <span>Secure payment powered by Stripe</span>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <AlertCircle className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Online payments aren't enabled for this business yet.</p>
                {invoice.business_phone && (
                  <p className="text-xs text-slate-400 mt-2">Please contact {invoice.business_name} at {invoice.business_phone}.</p>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-300 mt-6">Powered by Fieldz AI</p>
      </div>
    </div>
  );
};

export default PublicInvoicePay;
